import { useMemo, useState } from 'react';
import { Alert, Card, Typography } from 'antd';
import { exportIxTableCsv, generateIxTable } from '../../api/data';
import { IxTableRequest, IxTableResult, IxTableType } from '../../@types/ghePage5/types';
import { useSelected } from '../../providers/selectedContext';
import { TextField } from '../../design-system';
import { GheIxCsvDownloadButton } from './GheIxCsvDownloadButton';
import { GheIxTableForm } from './GheIxTableForm';
import { GheIxTablePreview } from './GheIxTablePreview';
import styles from '../gheShared/gheStickyHeader.module.css';
import layoutStyles from './GheIxTablesTab.module.css';

export const GheIxTablesTab = ({ actions }: { actions?: import('react').ReactNode }) => {
  const { selectedInfo } = useSelected();
  const projectUid = selectedInfo.project?.uid || '';
  const [tableType, setTableType] = useState<IxTableType>('enthalpy');
  const [tableFilename, setTableFilename] = useState('tables_export');
  const [result, setResult] = useState<IxTableResult | null>(null);
  const [lastRequest, setLastRequest] = useState<IxTableRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRender = useMemo(() => Boolean(projectUid), [projectUid]);

  const buildDownloadFilename = (baseName?: string) => {
    const sanitized = (baseName || 'tables_export').trim().replace(/[^A-Za-z0-9_.-]+/g, '_');
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    const timestamp = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}__${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
    return `${sanitized || 'tables_export'}_${timestamp}.csv`;
  };

  if (!canRender) {
    return (
      <Alert
        type="info"
        showIcon
        message="Select a project to use IX tables."
        data-testid="ghe-page5-no-project"
      />
    );
  }

  const handleGenerate = async (payload: IxTableRequest) => {
    setError(null);
    setLoading(true);
    try {
      const generatedResult = await generateIxTable(projectUid, payload);
      const normalizedResult = generatedResult
        ? {
            ...generatedResult,
            row_count:
              typeof generatedResult.row_count === 'number'
                ? generatedResult.row_count
                : typeof (generatedResult as IxTableResult & { total_rows?: number }).total_rows === 'number'
                ? (generatedResult as IxTableResult & { total_rows?: number }).total_rows || 0
                : Array.isArray(generatedResult.rows)
                ? generatedResult.rows.length
                : 0
          }
        : generatedResult;
      setResult(normalizedResult);
      setLastRequest(payload);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Unable to generate IX table.';
      setError(message);
      setResult(null);
      setLastRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!lastRequest) {
      return;
    }

    setExporting(true);
    try {
      const csvData = await exportIxTableCsv(projectUid, lastRequest);
      const blob = csvData instanceof Blob ? csvData : new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildDownloadFilename(lastRequest.table_filename);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Unable to export CSV.';
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const handleTableTypeChange = (value: IxTableType) => {
    setTableType(value);
    setResult(null);
    setLastRequest(null);
    setError(null);
  };

  return (
    <div data-testid="ghe-page5-tab" className={styles.tab}>
      <div className={styles.stickyHeader}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            IX Tables
          </Typography.Title>
          <Typography.Text type="secondary">
            Generate Enthalpy, K-values, and CO2 solubility tables and export as CSV.
          </Typography.Text>
        </div>
        {actions}
      </div>
      <div className={styles.content}>
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            data-testid="ghe-page5-generate-error"
          />
        )}
        <div className={layoutStyles.columns}>
          <div className={layoutStyles.column}>
            <Card>
              <GheIxTableForm
                projectUid={projectUid}
                tableType={tableType}
                tableFilename={tableFilename}
                onTableTypeChange={handleTableTypeChange}
                onGenerate={handleGenerate}
                generating={loading}
              />
            </Card>
          </div>
          <div className={layoutStyles.column}>
            <Card>
              <GheIxTablePreview result={result} />
              {result?.prediction_metadata && (
                <Typography.Text
                  type="secondary"
                  data-testid="ghe-page5-metadata"
                >
                  Prediction path: {result.prediction_metadata.path}
                </Typography.Text>
              )}
              {result?.ift_usage_provenance && (
                <Typography.Text
                  type="secondary"
                  data-testid="ghe-page5-ift-result-provenance"
                  style={{ display: 'block' }}
                >
                  IFT path: {result.ift_usage_provenance.prediction_path}
                  {result.ift_usage_provenance.artifact_revision
                    ? ` (${result.ift_usage_provenance.artifact_revision})`
                    : ''}
                </Typography.Text>
              )}
            </Card>
            <TextField
              label="Table file name"
              fullWidth
              value={tableFilename}
              onChange={event => setTableFilename(event.target.value)}
              data-testid="ghe-page5-table-filename"
            />
            <GheIxCsvDownloadButton
              disabled={!result || !lastRequest}
              loading={exporting}
              onDownload={handleDownload}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
